// Thin fetch wrapper around the {success, data} / {success, error} envelope.

export interface ApiErrorShape {
  code: string;
  message: string;
}

export class ApiClientError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly httpStatus: number,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

interface Envelope<T> {
  success: boolean;
  data?: T;
  error?: ApiErrorShape;
}

export async function apiFetch<T>(
  input: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(input, {
    ...init,
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  let body: Envelope<T> | null = null;
  try {
    body = (await res.json()) as Envelope<T>;
  } catch {
    /* non-JSON response */
  }

  if (!res.ok || !body?.success) {
    const err = body?.error;
    throw new ApiClientError(
      err?.code ?? "INTERNAL_ERROR",
      err?.message ?? "通信に失敗しました",
      res.status,
    );
  }

  return body.data as T;
}

// Admin API calls. In production, Cloudflare Access injects the
// Cf-Access-Authenticated-User-Email header server-side, so nothing extra is
// needed. In local dev only, we attach X-Dev-Admin-Email so the Functions
// accept the request (see server/lib/auth.ts).
export function adminFetch<T>(input: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (import.meta.env.DEV) {
    headers.set("X-Dev-Admin-Email", "dev@example.com");
  }
  return apiFetch<T>(input, { ...init, headers });
}
